// Hideo - the desktop app (Tauri, src-tauri/; docs/architecture.md, "Desktop app"): whether the app runs inside it, and
// its native files: "Save as" for exports and a data folder chosen once (src/features/storage/folder.ts). The Tauri
// modules are imported only when needed, so the website never loads them.
import { t } from '../i18n'
import { uiAlert } from '../stores/notices'

/** The app runs inside the desktop app */
export const isDesktop = (): boolean => typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window

/** Path of the data folder chosen in the desktop app ('' = none: the data stays in the app's storage, as on the website) */
const FOLDER_KEY = 'creatorOS_desktopFolder'
export function desktopFolderPath(): string { try { return localStorage.getItem(FOLDER_KEY) || '' } catch { return '' } }
export function setDesktopFolderPath(path: string): void {
  try { if (path) localStorage.setItem(FOLDER_KEY, path); else localStorage.removeItem(FOLDER_KEY) } catch { /* blocked storage */ }
}

/** The system's folder picker; the chosen folder (and what is inside it) becomes readable and writable, also after a
 *  restart (persisted-scope plugin). Null if cancelled */
export async function pickDesktopFolder(): Promise<string | null> {
  const { open } = await import('@tauri-apps/plugin-dialog')
  const r = await open({ directory: true, recursive: true, title: t('Choose the folder for your data') })
  return typeof r === 'string' ? r : null
}

/** "Save as" with the system's window, then the text is written there. Returns false if cancelled */
export async function saveTextAs(name: string, text: () => Promise<string> | string): Promise<boolean> {
  const { save } = await import('@tauri-apps/plugin-dialog')
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : ''
  const path = await save({ defaultPath: name, filters: ext ? [{ name: ext.toUpperCase(), extensions: [ext] }] : [] })
  if (!path) return false
  try { await (await import('@tauri-apps/plugin-fs')).writeTextFile(path, await text()) }
  catch (e) { console.error(e); uiAlert(t('Saving the file failed.')); return false }
  return true
}

/** Downloads a text file: on the website the browser's download, in the desktop app "Save as" */
export function downloadText(name: string, text: string, type: string): void {
  if (isDesktop()) { void saveTextAs(name, () => text); return }
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([text], { type })); a.download = name
  document.body.appendChild(a); a.click()
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 1000)
}
